import type { Dictionary } from './fr';

/**
 * Dictionnaire PORTUGAIS (BRÉSIL).
 *
 * ⚠️ Traduction automatique relue par IA (Claude, 17/10/2026), pas par un locuteur natif.
 *
 * Registre : portugais brésilien courant, « você », impératif en forme « você » (« Crie », « Tente »).
 * Glossaire : alternance → « alternância » (pas « aprendizagem », qui évoque le Jovem Aprendiz) ;
 * stage → « estágio » ; CDI / CDD → sigle toujours gardé, suivi d'une glose : « CDI (efetivo) » /
 * « CDD (prazo determinado) » ; CV → « currículo » (acronyme ATS gardé) ;
 * offre (onglet Offres) → « vagas » ; fiche de poste → « descrição da vaga » ; annonce → « anúncio » ;
 * lettre de motivation → « carta de apresentação » ; notation → « avaliação » ; école → « escola »,
 * CFA → sigle gardé (« CFAs ») ; créneau → « horário » ; formule (coaching) → « formato » ;
 * Permis B → « CNH B ». Plans : Free / Plus / Pro / École gardés comme en anglais, « Gratuit » → « Grátis ».
 */
export const pt: Dictionary = {
  common: {
    back: 'Voltar',
    scoreSur100: 'de 100',
    cancel: 'Cancelar',
    save: 'Salvar',
    retry: 'Tentar de novo',
    delete: 'Excluir',
    ok: 'Entendi',
  },

  a11y: {
    close: 'Fechar',
    removeLanguage: 'Remover o idioma',
    removeBullet: 'Remover o ponto',
    removeProject: 'Remover o projeto',
    removeEducation: 'Remover a formação',
    removeExperience: 'Remover a experiência',
    removeTag: 'Remover %{tag}',
    useful: 'Útil',
    notUseful: 'Não é útil',
    listen: 'Ouvir esta mensagem',
    notifications: 'Notificações',
    settings: 'Configurações',
    changePhoto: 'Alterar a foto de perfil',
    changeSearchTarget: 'Alterar seu objetivo de busca',
    importPhoto: 'Importar uma foto',
    score: 'Pontuação: %{score} de 100',
  },

  nav: {
    carriere: 'Carreira',
    offers: 'Vagas',
    news: 'Notícias',
    profile: 'Perfil',
    aiAction: 'Ação IA',
  },

  home: {
    content: {
      seeAll: 'Ver tudo',
      new: 'NOVO',
      duration: '%{minutes} min',
      play: 'Ouvir',
      types: {
        podcast: 'Podcast AlternJob',
        tip: 'A dica do dia',
        event: 'Não perca',
        success: 'Eles conseguiram',
        market: 'Mercado e vagas',
        masterclass: 'Masterclass',
        challenge: 'Desafio',
        news: 'Novidade na Horméia',
      },
      sample: {
        title: 'IA e recrutamento: o que realmente muda em 2024',
        description: 'Dicas, depoimentos e tendências do mercado de trabalho.',
      },
    },
  },


  carriere: {
    title: 'Carreira',
    headline: 'Seja o candidato\nque os recrutadores',
    headlineAccent: 'disputam.',
    position: 'Cartão %{index} de %{total}',
    cv: {
      title: 'Currículo ATS',
      description:
        'Crie um currículo profissional, otimizado para passar pelos filtros e chamar a atenção dos recrutadores.',
      cta: 'Analisar meu currículo',
    },
    linkedin: {
      title: 'LinkedIn',
      description:
        'Crie um perfil profissional e aumente sua visibilidade para recrutadores e empresas.',
      cta: 'Melhorar meu perfil',
    },
    letter: {
      title: 'Carta de apresentação',
      description: 'Escreva cartas marcantes e personalizadas para cada candidatura.',
      cta: 'Escrever minha carta',
    },
    interview: {
      title: 'Entrevista com IA',
      description: 'Treine e mande bem em todas as suas entrevistas.',
      cta: 'Treinar',
    },
    // Page Carrière sur grand écran : panneau « prochain pas », chiffres et état de chaque outil.
    tableau: {
      prochainPas: 'Seu próximo passo para conseguir entrevistas',
      premierPas: 'Comece por saber onde você está',
      conseilParDefaut: 'Seu currículo ainda pode melhorar: abra o detalhe para ver as correções.',
      sansScore: 'Envie seu currículo: você recebe uma nota ATS e as correções que importam, em segundos.',
      corriger: 'Corrigir meu currículo',
      noter: 'Avaliar meu currículo',
      chiffreCv: { one: 'currículo salvo', other: 'currículos salvos' },
      chiffreLettres: { one: 'carta escrita', other: 'cartas escritas' },
      chiffreCorrections: { one: 'correção sugerida', other: 'correções sugeridas' },
      statutCvAucun: 'Nenhum currículo ainda',
      statutCv: { one: '%{count} currículo', other: '%{count} currículos' },
      statutScore: 'nota %{score}',
      statutLettreAucune: 'Nenhuma carta ainda',
      statutLettres: { one: '%{count} carta escrita', other: '%{count} cartas escritas' },
      statutLinkedin: 'Auditoria em um minuto',
      statutInterview: 'Recrutador ou gestor',
    },
  },

  offers: {
    title: 'Vagas',
    subtitle: 'Em breve, vagas selecionadas para o seu perfil e as suas buscas.',
    soonTitle: 'Nova funcionalidade em construção',
    soonBody:
      'Estamos preparando uma seleção de vagas de alternância (estudo e trabalho) e de primeiro emprego que combinam com o seu currículo e com o que você procura. Enquanto isso, você pode trabalhar seu currículo, seu perfil do LinkedIn e suas cartas na aba Carreira.',
  },

  offres: {
    franceUniquement: 'As vagas são apenas para cargos na França e estão escritas em francês.',
    titre: 'Vagas',
    sousTitre: 'Escolhidas com base nos seus critérios',
    credits: { zero: 'Nenhuma candidatura IA restante este mês', one: '%{count} candidatura IA restante', other: '%{count} candidaturas IA restantes' },
    segments: { pourToi: 'Para você', sauvegardees: 'Salvas' },
    modifierCriteres: 'Editar meus critérios',
    sources: { france_travail: 'France Travail', lba: 'La bonne alternance', adzuna: 'Jobs by Adzuna' },
    contrats: { alternance: 'Alternância', stage: 'Estágio', cdi: 'CDI (efetivo)', cdd: 'CDD (prazo determinado)', autre: 'Outro contrato' },
    contratsEnLigne: { alternance: 'alternância', stage: 'estágio', cdi: 'CDI (efetivo)', cdd: 'CDD (prazo determinado)', autre: 'outro contrato' },
    rythmes: {
      '3j2j': '3 d / 2 d',
      '4j1j': '4 d / 1 d',
      '1sem1sem': '1 sem / 1 sem',
      '2sem2sem': '2 sem / 2 sem',
      '3sem1sem': '3 sem / 1 sem',
      '2j3j': '2 d / 3 d',
    },
    rythmesComplets: {
      '3j2j': '3 dias na empresa, 2 dias na escola',
      '4j1j': '4 dias na empresa, 1 dia na escola',
      '1sem1sem': '1 semana na empresa, 1 semana na escola',
      '2sem2sem': '2 semanas na empresa, 2 semanas na escola',
      '3sem1sem': '3 semanas na empresa, 1 semana na escola',
    },
    dureesStage: { '2': '2 meses', '3': '3 meses', '4': '4 meses', '5': '5 meses', '6': '6 meses' },
    dureesCdd: { lt6: 'Menos de 6 meses', '6a12': '6 a 12 meses', '12plus': '1 ano ou mais' },
    rayonKm: '%{km} km',
    preferences: {
      titre: 'Seus critérios',
      sousTitre: 'Buscamos com base no que você escolher aqui.',
      contratMulti: 'você pode escolher vários',
      peuImporte: 'Tanto faz',
      cdiSeul: 'Em um contrato efetivo (CDI), não há mais nada a escolher.',
      verifier: 'Confira os campos indicados acima.',
      metier: 'Profissão desejada',
      metierPlaceholder: 'Ex.: analista de comunicação digital',
      contrat: 'Tipo de contrato',
      ville: 'Cidade',
      villePlaceholder: 'Ex.: Lyon',
      rayon: 'Raio',
      rythme: 'Ritmo da alternância',
      rythmeAide: 'Empresa / escola',
      dureeStage: 'Duração do estágio',
      dureeCdd: 'Duração do contrato a termo',
      dureeAide: 'Ofertas que indicam outra duração são descartadas.',
      reponsesTitre: 'Respostas-chave, nunca inventadas',
      permis: 'CNH B (permis B)',
      oui: 'Sim',
      non: 'Não',
      aRenseigner: 'A preencher',
      disponibilite: 'Disponível a partir de',
      disponibilitePlaceholder: 'DD/MM/AAAA',
      voir: 'Ver minhas vagas',
      metierRequis: 'Informe a profissão que você busca.',
      villeRequise: 'Informe sua cidade.',
      villeIntrouvable: 'Cidade não encontrada na França. Verifique a grafia ou escolha uma cidade francesa.',
      villeRetenue: 'Cidade entendida: %{commune}. Se não for a certa, corrija; se for, confirme novamente.',
      contratRequis: 'Escolha pelo menos um tipo de contrato.',
      disponibiliteInvalide: 'Escreva a data no formato DD/MM/AAAA.',
      erreurEnregistrement: 'Não foi possível salvar seus critérios. Tente de novo.',
      chargement: 'Carregando seus critérios…',
      retour: 'Voltar às vagas',
    },
    raisons: {
      contrat: 'Contrato: %{valeur}',
      contrat_different: 'Contrato diferente da sua busca',
      distance: 'A %{valeur} km da sua cidade',
      distance_loin: 'A %{valeur} km, fora do seu raio',
      distance_inconnue: 'Local de trabalho não informado',
      distance_rayon: 'Dentro do seu raio de %{valeur} km',
      rythme: 'Ritmo %{valeur}, como você quer',
      rythme_different: 'Ritmo %{valeur}, diferente do seu',
      rythme_absent: 'Ritmo da alternância não informado',
      metier: 'Cargo próximo da sua profissão: “%{valeur}”',
      metier_partiel: 'Cargo parcialmente próximo: “%{valeur}”',
      metier_rome: 'Mesma família de profissões que a sua (código ROME %{valeur})',
      metier_eloigne: 'Cargo distante da profissão que você busca',
      metier_sans_citation: 'Corresponde à profissão que você busca',
      metier_partiel_sans_citation: 'Próximo da profissão que você busca',
      permis_a_renseigner: 'CNH B exigida: a preencher',
      permis_manquant: 'CNH B exigida',
    },
    carte: {
      pourquoi: 'Por que combina',
      publiee: { one: '%{source} · publicada há %{jours} d', other: '%{source} · publicada há %{jours} d' },
      publieeAujourdhui: '%{source} · publicada hoje',
      voirDetail: 'Ver detalhes',
      voirOffreComplete: 'Ver a vaga completa',
      contratDuree: '%{contrat} · %{duree}',
      dureeMois: { one: '%{count} mês', other: '%{count} meses' },
      salaire: 'Salário: %{valeur}',
      entrepriseInconnue: 'Empresa não informada',
      lieuDistance: '%{lieu} · %{km} km',
      score: 'Pontuação de match: %{score} de 100',
    },
    actions: {
      passer: 'Pular esta vaga',
      sauvegarder: 'Salvar esta vaga',
      interesse: 'Tenho interesse nesta vaga',
      aide: 'Deslize para a direita se a vaga te interessar, para a esquerda se não',
      libelles: { passer: 'Pular', sauvegarder: 'Salvar', interesse: 'Tenho interesse' },
    },
    detail: {
      voirCandidature: 'Ver minha candidatura',
      alerteMineur: 'Atenção: esta vaga parece envolver %{motifs}, proibido ou restrito para menores de 18 anos.',
      motifsMineur: { nuit: 'trabalho noturno', alcool: 'serviço de bebidas alcoólicas', hauteur: 'trabalho em altura' },
      fermer: 'Fechar os detalhes',
      monMatch: 'Seu match',
      description: 'Descrição',
      extrait: 'Trecho do anúncio',
      extraitAide: 'A Adzuna só envia o começo do anúncio. O texto completo está no site deles.',
      voirOffreComplete: 'Ver a vaga completa',
      entreprise: 'A empresa',
      infos: 'Informações',
      voirOffre: 'Ver o anúncio original',
      preparer: 'Preparar minha candidatura',
      source: 'Fonte: %{source}',
      majLe: 'Atualizada em %{date}',
      licence: 'Reutilização sujeita à licença France Travail',
      retiree: 'Esta vaga foi retirada pelo recrutador.',
      logo: 'Logo de %{entreprise}',
      sauvegardee: 'Vaga salva',
      rubriques: {
        nature_contrat: 'Natureza do contrato',
        type_contrat: 'Tipo de contrato',
        duree_travail: 'Jornada de trabalho',
        experience: 'Experiência',
        formations: 'Formação',
        competences: 'Competências',
        qualites: 'Qualidades profissionais',
        savoirs: 'Ferramentas',
        langues: 'Idiomas',
        permis: 'Habilitação',
        salaire: 'Salário',
        secteur: 'Setor de atividade',
        qualification: 'Qualificação',
        deplacements: 'Deslocamentos',
        conditions: 'Condições de trabalho',
        postes: 'Número de vagas',
        accessible_th: 'Aberta a pessoas com deficiência',
        contact: 'Contato',
        agence: 'Agência France Travail',
        duree_contrat: 'Duração do contrato',
        debut: 'Início do contrato',
        expiration: 'Vaga válida até',
        rome: 'Código ROME',
      },
    },
    preparation: {
      parallele: 'AO MESMO TEMPO',
      motsClesTitre: 'PALAVRAS-CHAVE ENCONTRADAS NA VAGA',
      motsClesAutres: '+%{count}',
      motsClesA11y: { one: '1 palavra-chave encontrada: %{liste}', other: '%{count} palavras-chave encontradas: %{liste}' },
      motsClesCouvertsA11y: '%{couverts} de %{total} palavras-chave cobertas pelo seu currículo: %{liste}',
      titre: 'Sua IA está preparando sua candidatura',
      pret: 'Sua candidatura está pronta',
      pour: 'Para %{entreprise} · %{poste}',
      pourPoste: 'Para %{poste}',
      fermer: 'Fechar a preparação',
      terminer: 'Fechar',
      verifier: 'Revisar minha candidatura',
      reessayer: 'Tentar de novo',
      retourOffres: 'Voltar às vagas',
      pied: 'Nada é enviado sem a sua aprovação',
      progression: 'Preparação da candidatura',
      etapes: { analyse: 'Análise da vaga', cv: 'Currículo adaptado à vaga', lettre: 'Carta de apresentação', controle: 'Controle de qualidade' },
      attente: 'Aguardando',
      enCours: 'Em andamento…',
      echecEtape: 'Esta etapa falhou',
      motsCles: { zero: 'nenhuma palavra-chave', one: '1 palavra-chave', other: '%{count} palavras-chave' },
      questions: { zero: 'nenhuma pergunta', one: '1 pergunta', other: '%{count} perguntas' },
      analyseFaite: '%{motsCles}, %{questions} identificadas',
      cvFait: { zero: 'Nenhuma palavra-chave incluída', one: '1 palavra-chave incluída', other: '%{count} palavras-chave incluídas' },
      titreAligne: ', título alinhado à vaga',
      lettreFaite: 'Carta de %{count} palavras',
      controleFait: { zero: 'Nenhum ponto a confirmar', one: '1 ponto a confirmar', other: '%{count} pontos a confirmar' },
      couverture: 'Palavras-chave cobertas: %{couverts} de %{total}',
      erreurs: {
        quota: 'Você usou todas as suas candidaturas IA este mês.',
        cv_manquant: 'Você precisa de um currículo principal para preparar uma candidatura. Crie ou importe seu currículo em Carreira.',
        offre_retiree: 'Esta vaga não está mais no ar: a candidatura não pode ser preparada.',
        profil_trop_eloigne: 'Esta vaga está distante demais do seu currículo para escrever uma carta convincente.',
        echec: 'Uma etapa falhou. Tente de novo: as etapas já feitas ficam salvas.',
      },
    },
    avant: {
      fermer: 'Fechar',
      naissanceTitre: 'Sua data de nascimento',
      naissanceRaison: 'Ela serve para ajustar o tom da sua carta e avisar se uma vaga não é permitida para menores de 18 anos. Você só informa uma vez.',
      naissanceFormat: 'DD/MM/AAAA',
      erreurNaissance: 'Não foi possível salvar sua data de nascimento. Tente de novo.',
      continuer: 'Continuar',
      annuler: 'Cancelar',
      eloigneeTitre: 'Esta vaga está longe do seu perfil',
      eloigneeTexte: 'Seu match é de %{score} de 100. Você pode preparar a candidatura mesmo assim: um crédito será usado se ela for concluída.',
      prepareQuandMeme: 'Preparar mesmo assim',
      nePasPreparer: 'Não preparar',
    },
    relecture: {
      titre: 'Revise sua candidatura',
      fermer: 'Fechar a revisão',
      erreurLecture: 'Não foi possível carregar sua candidatura.',
      reessayer: 'Tentar de novo',
      manquants: 'Ausentes do seu currículo: %{liste}',
      aConfirmer: 'Verifique antes de enviar',
      raisons: {
        nombre: 'Número ausente do seu currículo',
        date: 'Data ausente do seu currículo',
        competence: 'Competência que seu currículo não mostra',
        a_confirmer: 'A confirmar',
      },
      exact: 'Sim, está correto',
      corriger: 'Corrigir',
      enregistrer: 'Salvar',
      annuler: 'Cancelar',
      champCorrection: 'Sua correção',
      confirme: 'Confirmado',
      corrige: 'Corrigido',
      erreurCorrection: 'Não foi possível salvar esta correção. Tente de novo.',
      onglets: { cv: 'Currículo', lettre: 'Carta', reponses: 'Respostas' },
      legende: 'Destacado: alterado pela IA para esta vaga.',
      formation: 'Formação',
      competences: 'Competências',
      langues: 'Idiomas',
      objet: 'Assunto:',
      copierLettre: 'Copiar a carta',
      erreurCopie: 'Não foi possível abrir o compartilhamento. Tente de novo.',
      reponses: {
        oui: 'Sim',
        non: 'Não',
        date: 'Disponível em %{date}',
        age: '%{age} anos',
        aRenseigner: 'A preencher',
        aucune: 'Nenhuma pergunta eliminatória encontrada nesta vaga.',
      },
      exporterCv: 'Exportar o currículo (PDF)',
      exporterLettre: 'Exportar a carta (PDF)',
      erreurExport: 'A exportação falhou. Tente de novo.',
      voirOffre: 'Ver a vaga',
      voirOffreComplete: 'Ver a vaga completa',
      extrait: 'Preparada a partir de um trecho do anúncio: leia a vaga completa antes de enviar sua candidatura.',
    },
    etats: {
      chargement: 'Buscando suas vagas…',
      erreurTitre: 'Não foi possível carregar as vagas',
      erreurTexte: 'Verifique sua conexão e tente de novo.',
      reessayer: 'Tentar de novo',
      videTitre: 'Nenhuma vaga para esses critérios',
      videTexte: 'Aumente o raio ou tente uma profissão parecida.',
      videTexteDuree: 'Aumente o raio, tente uma profissão parecida ou flexibilize a duração do contrato.',
      epuiseTitre: 'Você viu todas as vagas do momento',
      epuiseTexte: 'Volte mais tarde ou amplie seus critérios para ver outras.',
      sourcePartielle: 'Uma fonte de vagas não está respondendo: a lista pode estar incompleta.',
      sauvegardeesVideTitre: 'Nenhuma vaga salva',
      sauvegardeesVideTexte: 'Toque no marcador de uma vaga para encontrá-la aqui.',
      retiree: 'Vaga retirada',
      decisionEchec: 'Não foi possível salvar sua escolha. Verifique sua conexão.',
    },
  },

  news: {
    title: 'Notícias',
    segments: { forYou: 'Para você', favorites: 'Favoritos' },
    verified: 'Editor verificado',
    readingTime: '%{count} min de leitura',
    relative: {
      today: 'Hoje',
      yesterday: 'Ontem',
      days: { one: 'Há %{count} d', other: 'Há %{count} d' },
      weeks: { one: 'Há %{count} sem.', other: 'Há %{count} sem.' },
      months: { one: 'Há %{count} mês', other: 'Há %{count} meses' },
      years: { one: 'Há %{count} ano', other: 'Há %{count} anos' },
    },
    like: {
      add: 'Curtir este artigo',
      remove: 'Descurtir',
      count: {
        zero: 'Nenhuma curtida',
        one: '%{count} curtida',
        other: '%{count} curtidas',
      },
      failed: 'Não foi possível atualizar esta curtida.',
    },
    favorite: {
      add: 'Adicionar aos favoritos',
      remove: 'Remover dos favoritos',
      added: 'Nos favoritos',
      notAdded: 'Fora dos favoritos',
      failed: 'Não foi possível atualizar este favorito.',
    },
    errorTitle: 'Não foi possível carregar o feed',
    errorBody: 'Verifique sua conexão e tente de novo.',
    emptyTitle: 'Nenhum artigo por enquanto',
    emptyBody: 'Os novos artigos vão aparecer aqui assim que forem publicados.',
    favoritesEmptyTitle: 'Nenhum favorito',
    favoritesEmptyBody: 'Toque no marcador de um artigo para encontrá-lo aqui.',
    favoritesSignedOutTitle: 'Entre para guardar seus artigos',
    favoritesSignedOutBody: 'Seus favoritos ficam vinculados à sua conta e te acompanham em todos os aparelhos.',
    end: 'Você está em dia.',
  },

  notifications: {
    welcome: {
      title: 'Boas-vindas à Horméia 👋',
      body: 'Comece gerando um currículo otimizado para ATS e depois treine a entrevista com a IA. Duas etapas, e você consegue mais entrevistas.',
    },
    quantify: {
      title: 'Coloque números nos seus resultados',
      body: 'No seu currículo, troque “gestão de redes sociais” por “+35% de engajamento em 3 meses”. Os recrutadores e os filtros ATS prestam atenção aos números.',
    },
    followUp: {
      title: 'Faça um follow-up após 7 dias',
      body: 'Sem resposta uma semana depois da candidatura? Mande uma mensagem curta e educada ao recrutador: um follow-up bem feito muitas vezes dobra as chances de resposta.',
    },
  },

  myCv: {
    title: 'Meu currículo',
    scoreLabel: 'Sua pontuação ATS',
    scoreNone: 'Faça uma primeira avaliação para descobrir sua pontuação ATS.',
    scoreClean: 'Nenhuma melhoria pendente. Seu currículo está pronto.',
    scoreAdvice: {
      one: '%{count} melhoria ainda pode aumentar suas chances.',
      other: '%{count} melhorias ainda podem aumentar suas chances.',
    },
    scoreActionFirst: 'Avaliar meu currículo',
    scoreActionDetail: 'Ver detalhes',
    createOrImport: 'Criar ou importar',
    importTitle: 'Importar meu currículo',
    importSubtitle: 'PDF ou Word',
    createTitle: 'Criar um currículo',
    createSubtitle: 'A partir de um modelo',
    improve: 'Melhorar meu currículo',
    scoreRowTitle: 'Avaliar meu currículo',
    scoreRowSubtitle: 'Pontuação ATS e prioridades',
    optimiseTitle: 'Otimizar meu currículo',
    optimiseSubtitle: 'Melhore o que você já tem',
    myCvs: 'Meus currículos',
    savedCount: {
      zero: 'Nenhum currículo salvo',
      one: '%{count} currículo salvo',
      other: '%{count} currículos salvos',
    },
    savedListOpen: 'Ver meus currículos salvos',
    listSubtitle: 'Encontre, reabra e gerencie seus currículos',
    priorityLabel: 'Corrija isto primeiro',
    importShort: 'Importar',
    createShort: 'Criar um currículo',
    cvNote: 'Currículo avaliado',
    changer: 'Trocar',
  },

  cvList: {
    title: 'Meus currículos',
    subtitle: 'Encontre, reabra e gerencie seus currículos salvos.',
    loadError: 'Não foi possível carregar seus currículos. Tente de novo.',
    emptyTitle: 'Nenhum currículo por enquanto.',
    emptyText: 'Crie seu primeiro currículo otimizado para ATS em poucos minutos.',
    emptyCta: 'Criar meu currículo',
    primary: 'Principal',
    setPrimary: 'Definir como principal',
    edit: 'Editar',
    preview: 'Pré-visualizar',
    others: 'Meus outros currículos',
    score: 'Pontuação ATS %{score}',
    more: 'Mais ações',
    deleteTitle: 'Excluir este currículo?',
    deleteBody: '“%{title}” será excluído definitivamente.',
  },

  cvImprove: {
    title: 'Otimizar meu currículo',
    subtitle: 'A IA propõe alterações precisas, e você fica com as que quiser.',
    loading: 'A IA está analisando seu currículo…',
    blocked: 'Este recurso é exclusivo para assinantes.',
    notFound: 'Currículo não encontrado. Volte à avaliação e tente de novo.',
    saveFailed: 'Não foi possível salvar.',
    reponseInvalide: 'A resposta veio incompleta. Tente de novo em instantes.',
    defaultTitle: 'Currículo otimizado',
    choixQuestion: 'Quer importar uma descrição da vaga para que seu currículo combine melhor com ela?',
    choixDeja: 'Você avaliou seu currículo com uma descrição de vaga. Quer usá-la para otimizá-lo?',
    choixNon: 'Não, otimizar direto',
    choixOui: 'Sim, adicionar uma descrição de vaga',
    choixUtiliser: 'Usar a descrição já importada',
    ficheTitre: 'Descrição da vaga',
    ficheSousTitre: 'Seu currículo será adaptado a esta vaga, sem inventar nada.',
    ficheChamp: 'CAPTURAS DE TELA, PDF OU TEXTO',
    ficheAide: 'Combine se precisar: tudo será lido como uma única vaga. No máximo 5 capturas e 8 MB, PDF de até 20 páginas.',
    optimiser: 'Otimizar meu currículo',
    passer: 'Pular',
    ficheUtilisee: 'Adaptado à descrição de vaga enviada.',
    // Optimisation par modifications (1.2.0).
    revue: {
      titre: {
        one: '%{count} alteração proposta',
        other: '%{count} alterações propostas',
      },
      aide: 'Aceite ou recuse cada alteração. Seu currículo só muda quando você aplicar.',
      aideTitre: 'Aceite ou recuse cada alteração.',
      aideSuite: 'Seu currículo só muda quando você aplicar.',
      // Libellé SEUL, posé à côté du nombre affiché dans sa tuile (le titre complet reste `titre`).
      libelle: { one: 'alteração proposta', other: 'alterações propostas' },
      score: 'O currículo alterado não é avaliado de novo: avalie-o depois para saber a pontuação ATS.',
    },
    tags: { impact: 'Impacto', action: 'Verbo de ação', keywords: 'Palavras-chave', clarity: 'Clareza', title: 'Título' },
    modif: { avant: 'Antes', apres: 'Depois', appliquer: 'Aplicar: %{nom}', titreCv: 'Título do currículo' },
    pied: {
      retenues: 'Escolhidas: %{count} de %{total}',
      appliquer: {
        one: 'Aplicar %{count} alteração',
        other: 'Aplicar %{count} alterações',
      },
      aucune: 'Nenhuma alteração escolhida',
    },
    vide: { titre: 'Seu currículo já está sólido', detail: 'A IA não encontrou nenhuma alteração útil para propor. Nada foi descontado da sua cota.' },
    scoreInconnu: { titre: 'Pontuação não medida', detail: 'Avalie este currículo para saber a pontuação ATS.' },
  },

  ficheDePoste: {
    choisirOffre: 'Escolher uma vaga',
    chargement: 'Carregando suas vagas…',
    erreur: 'Não foi possível carregar suas vagas salvas.',
    vide: 'Nenhuma vaga salva. Salve uma vaga na aba Vagas para encontrá-la aqui.',
    coller: 'Colar o anúncio completo',
    compteur: '%{n} / %{max} caracteres',
    retirerCapture: 'Remover a captura %{n}',
    retirerFichier: 'Remover o arquivo',
    erreurs: {
      tropDeCaptures: 'No máximo %{max} capturas.',
      captureIllisible: 'Não foi possível ler uma captura. Escolha outra imagem.',
      tropLourd: 'As capturas passam de 8 MB no total. Remova uma para continuar.',
      pdfTropLong: 'Este PDF tem mais de 20 páginas. Mantenha só a descrição da vaga.',
    },
  },

  cvScore: {
    failed: 'A avaliação falhou. Tente novamente.',
    title: 'Avalie seu currículo',
    targetRoleLabel: 'Cargo desejado',
    targetRoleHint: 'O cargo que você busca: a IA também avalia o quanto seu currículo combina com ele.',
    targetRolePlaceholder: 'Ex. Desenvolvedor web em alternância',
    offerLabel: 'Descrição da vaga',
    offerHint: 'Adicione a vaga para ter uma nota de compatibilidade e dicas focadas nesse cargo.',
    offerPlaceholder: 'Cole a vaga aqui…',
    offerFile: 'Vaga (PDF/Word)',
    offerCaptures: 'Capturas',
    optional: 'Opcional',
    ficheAdded: 'Adicionada',
    moreAccurate: 'Para uma nota mais precisa',
    frise: { import: 'Importe', evalue: 'A IA avalia', score: 'Sua nota' },
    dropTitle: 'Solte seu currículo',
    dropSub: 'PDF ou Word. Você recebe sua nota ATS e dicas concretas.',
    dropReady: 'Currículo pronto para avaliar',
    dropTextReady: 'O texto do seu currículo está pronto.',
    dropPick: 'Escolher um arquivo',
    dropChange: 'Trocar de arquivo',
    hintEmpty: 'Importe um currículo para obter sua nota ATS.',
    hintReady: 'Pronto: sua nota chega em poucos segundos.',
    submit: 'Avaliar meu currículo',
    atsScore: 'Pontuação ATS',
    offerMatch: 'Compatibilidade com a vaga',
    strengths: 'PONTOS FORTES PARA ESTE CARGO',
    gaps: 'DIFERENÇAS EM RELAÇÃO À VAGA',
    saveImported: 'Salvar em Meus currículos',
    saveFailed: 'Não foi possível salvar. Tente de novo.',
    importedTitle: 'Currículo importado',
    improve: 'Otimizar meu currículo',
    verdict: { faible: 'Precisa melhorar', correct: 'Razoável, pode melhorar', bon: 'Bom currículo, alguns ajustes', excellent: 'Excelente, pronto para enviar' },
    resultDetail: {
      zero: 'Nenhuma melhoria pendente.',
      one: '%{count} melhoria ainda pode aumentar suas chances.',
      other: '%{count} melhorias ainda podem aumentar suas chances.',
    },
    advicePrio: 'Corrija isto primeiro',
    rescore: 'Avaliar de novo',
    rescoreTitre: 'Avaliar de novo?',
    rescoreMessage: 'Você volta ao formulário. A nota exibida será perdida.',
    rescoreAnnuler: 'Manter a nota',
    criteres: { titre: 'Detalhe por critério', keywords: 'Palavras-chave', layout: 'Layout para ATS', impact: 'Resultados quantificados', readability: 'Legibilidade' },
  },

  cvCreate: {
    questions: {
      role: { label: 'Cargo desejado', placeholder: 'Ex. Analista de projetos' },
      education: {
        label: 'Formação, diplomas e certificações',
        placeholder: 'Ex. Mestrado em Marketing, IAE Paris',
      },
      experiences: {
        label: 'Experiências profissionais (estágios, alternância, empregos)',
        placeholder: 'Descreva suas experiências profissionais',
      },
      projects: {
        label: 'Projetos pessoais (pessoais, voluntariado, side projects)',
        placeholder: 'Descreva seus projetos pessoais',
      },
      skills: { label: 'Competências e ferramentas', placeholder: 'Ex. SQL, SEO, Excel, Figma' },
      languages: { label: 'Idiomas' },
      interests: {
        label: 'Interesses e hobbies',
        placeholder: 'Ex. Corrida de trilha, fotografia, xadrez',
      },
      extra: {
        label: 'Algo a acrescentar? (opcional)',
        placeholder: 'Campo livre, a IA vai encaixar no lugar certo',
      },
    },
    steps: {
      analyse: 'Análise das suas respostas',
      format: 'Formatação do currículo',
      ats: 'Verificação ATS',
    },
  },

  languagePicker: {
    namePlaceholder: 'Idioma',
    level: 'Nível',
    diploma: 'Certificado (opcional)',
    diplomaPlaceholder: 'Ex. TOEFL 105 (ou deixe em branco)',
    add: 'Adicionar um idioma',
  },

  editors: {
    experience: {
      titre: 'Experiência',
      rowTitle: 'Experiência %{n}',
      roleLabel: 'Cargo',
      end: 'Fim',
      descriptionLabel: 'O que você fez',
      descriptionPlaceholder: 'Escreva de forma simples, a IA reformula.',
      role: 'Cargo',
      company: 'Empresa',
      start: 'Início',
      current: 'Cargo atual',
      description: 'O que você fez (a IA vai reescrever)',
      add: 'Adicionar uma experiência',
    },
    education: {
      titre: 'Formação',
      rowTitle: 'Formação %{n}',
      degree: 'Diploma / curso',
      school: 'Instituição',
      date: 'Data',
      current: 'Em andamento',
      add: 'Adicionar uma formação',
    },
    project: {
      titre: 'Projetos',
      rowTitle: 'Projeto %{n}',
      descriptionLabel: 'O que você realizou',
      descriptionPlaceholder: 'Escreva de forma simples, a IA reformula.',
      name: 'Nome do projeto',
      date: 'Data',
      current: 'Em andamento',
      description: 'O que você realizou (a IA vai reescrever)',
      add: 'Adicionar um projeto',
    },
    bullets: {
      placeholder: 'Descreva um resultado / uma ação',
      add: 'Adicionar um item',
    },
    template: {
      groupLabel: 'Modelo de currículo',
      mono: 'Profissional',
      accent: 'Colorido',
      accentColor: 'Cor de destaque',
      couleur: {
        lime: 'Lima',
        vert: 'Verde',
        bleu: 'Azul',
        beige: 'Bege',
        marron: 'Marrom',
        ardoise: 'Ardósia',
        marine: 'Azul-marinho',
      },
    },
    quantifyHint: 'Lembre de colocar números nos seus resultados (quantos, qual %, qual impacto). Isso aumenta sua pontuação ATS.',
    searchTarget: 'Você está buscando…',
  },

  cvEditor: {
    before: 'ANTES',
    after: 'DEPOIS - sugerido pela IA',
    reject: 'Recusar',
    accept: 'Aceitar',
    improveWithAi: 'Melhorar com a IA',
    headline: 'Título do currículo',
    experiences: 'Experiências',
    projects: 'Projetos',
    education: 'Formação',
    skills: 'Competências',
    languages: 'Idiomas',
    interests: 'Interesses',
    headlinePlaceholder: 'Ex. AI Ambassador & Community Coordinator',
    startPlaceholder: 'Ex. jan. 2024',
    endPlaceholder: 'Ex. Atual',
    projectDatePlaceholder: 'Ex. mar. 2024',
    educationDatePlaceholder: 'Ex. set. 2025',
    skillPlaceholder: 'Ex. SQL',
    languagePlaceholder: 'Ex. Inglês - C1',
    interestPlaceholder: 'Ex. Corrida de trilha',
    experienceLabel: 'Experiência',
    projectLabel: 'Projeto',
    startLabel: 'Início',
    endLabel: 'Fim',
    dateLabel: 'Data',
    rewriteRetry: 'Tente novamente.',
  },

  qualityCheck: {
    title: 'O que foi melhorado',
    corrections: 'Correções',
    languagesRemoved: 'Idiomas removidos (nível abaixo de B2)',
    skillsNotProven: 'Competências da vaga não comprovadas',
    toValidate: 'Para você validar',
    removed: 'Itens removidos',
    recommendations: 'Recomendações',
  },

  linkedin: {
    sousScores: { titre: 'Detalhe por seção', title: 'Título', about: 'Sobre', experiences: 'Experiência' },
    kit: {
      failed: 'Não foi possível gerar o kit de otimização. Sua pontuação e suas prioridades foram mantidas.',
      retry: 'Tentar o kit de novo',
    },
    title: 'Auditoria do LinkedIn',
    subtitle: 'A IA lê seu perfil e diz o que melhorar.',
    verdict: { faible: 'Perfil a reforçar', correct: 'Perfil razoável, pode melhorar', bon: 'Bom perfil, alguns ajustes', excellent: 'Perfil excelente' },
    resultDetail: {
      zero: 'Nenhuma ação prioritária.',
      one: '%{count} ação prioritária para melhorar.',
      other: '%{count} ações prioritárias para melhorar.',
    },
    profilAnalyse: 'Perfil analisado',
    relancer: 'Refazer',
    relancerTitre: 'Refazer a auditoria?',
    relancerMessage: 'Você voltará ao formulário. A auditoria e o kit exibidos serão perdidos.',
    relancerAnnuler: 'Manter esta auditoria',
    languageLabel: 'Idioma da auditoria',
    languageHint: 'Seu perfil continua no idioma dele: a auditoria e o kit é que são escritos neste idioma.',
    addCaptures: 'Adicionar capturas',
    analyse: 'Analisar meu perfil',
    sourceConnect: 'Conta do LinkedIn',
    sourceShots: 'Capturas',
    capturesGras: 'Até 5 capturas',
    capturesSuite: ' do seu perfil, na ordem em que você quer que sejam lidas.',
    captureRemove: 'Remover a captura %{n}',
    profil: { titre: 'Título', apropos: 'Sobre', experiences: 'Experiência' },
    promesses: { audit: 'Uma auditoria detalhada', kit: 'Um kit para copiar' },
    hint: { empty: 'Adicione ao menos uma captura para iniciar a auditoria.', ready: 'Tudo pronto, você pode iniciar a auditoria.', connect: 'Conecte seu LinkedIn para iniciar a auditoria.', account: 'Seu perfil é lido diretamente da sua conta.' },
    missingKeywords: 'Palavras-chave a adicionar',
    priorities: 'Ações prioritárias',
    connection: {
      secureTitle: 'Conexão segura',
      secureBody: 'Acessamos só as informações necessárias para analisar seu perfil.',
      connected: 'LinkedIn conectado',
      connectedSub: 'Conexão segura, sem sua senha.',
      expired: 'Sessão do LinkedIn expirada. Conecte-se de novo para reativá-la.',
      unavailable: 'A conexão com o LinkedIn só está disponível na UE, no EEE e na Suíça.',
      unknown: 'Status do LinkedIn indisponível no momento.',
      failed: 'Não foi possível conectar ao LinkedIn. Tente de novo.',
      fallbackHint: 'As capturas de tela continuam funcionando.',
      eligibilityHint:
        'Disponível na UE, no EEE e na Suíça. Em outros lugares, use a aba Capturas — a análise é a mesma.',
      connect: 'Conectar meu LinkedIn',
      reconnect: 'Reconectar',
      disconnect: 'Desconectar',
    },
    optimisation: {
      v1: 'Versão 1 (direta para o recrutador)',
      v2: 'Versão 2 (palavras-chave)',
      v3: 'Versão 3 (diferenciada)',
    },
  },

  linkedinKit: {
    ready: 'Seu kit para copiar',
    readyHint: 'Toque em “Copiar” em um bloco, ou copie o kit inteiro, e cole no LinkedIn.',
    copy: 'Copiar',
    copied: 'Copiado',
    copyFailed: 'Falhou',
    copyAll: 'Copiar o kit inteiro',
    kitCopied: 'Kit copiado',
    kitCopyFailed: 'Não foi possível copiar: selecione o texto manualmente.',
    copyLabel: 'Copiar: %{nom}',
    headline: 'Título',
    about: 'Seção Sobre',
    aboutShort: 'Versão curta',
    aboutFull: 'Versão completa',
    experiences: 'Experiências-chave',
    skills: 'Competências',
    skillsPinned: 'Para fixar (5)',
    skillsExtra: 'Complementares',
    search: 'Preferências de busca',
    searchTitles: 'Cargos',
    searchContracts: 'Contratos',
    searchLocations: 'Localizações',
    searchSectors: 'Setores',
    searchAvailability: 'Disponibilidade',
    express: 'Dicas rápidas',
    photo: 'Foto',
    banner: 'Banner',
    url: 'URL',
    education: 'Formação (para colar)',
    quality: 'Controle de qualidade',
    keywordsAdded: 'Palavras-chave adicionadas',
    languagesRemoved: 'Idiomas removidos (abaixo de B2)',
    skillsNotProven: 'Competências não comprovadas',
    toValidate: 'Para validar',
    removed: 'Itens removidos',
    recommendations: 'Recomendações',
    recommended: 'Recomendado',
    experienceN: 'Experiência %{n}',
    recoAsk: 'Recomendação a pedir',
    recoAskWho: 'Recomendação a pedir (%{who})',
  },

  myLetter: {
    title: 'Minha carta de apresentação',
    subtitle: 'Crie sua carta de apresentação e depois peça uma análise.',
    pitchLead: 'Uma carta adaptada ao cargo faz toda a diferença.',
    arguments: {
      analysed: 'Analisada conforme o cargo',
      skills: 'Valoriza suas competências',
      structure: 'Estrutura convincente',
      ready: 'Pronta para enviar',
    },
    createSection: 'Criar minha carta',
    createTitle: 'Criar uma carta',
    createSubtitle: 'A partir de um dos seus currículos',
    listSection: 'Minhas cartas',
    listCount: 'Minhas cartas · %{count}',
    seeAll: 'Ver tudo',
    newLetter: 'Nova',
    listSubtitle: 'Encontre, reabra e gerencie suas cartas',
    improveSection: 'Melhorar minha carta',
    analyseTitle: 'Analisar minha carta',
    analyseSubtitle: 'Pontuação, erros detectados e prioridades',
    lastScore: 'Última pontuação',
    lastScoreNone: 'Peça a análise de uma carta para descobrir a pontuação.',
    savedCount: {
      zero: 'Nenhuma carta salva',
      one: '%{count} carta salva',
      other: '%{count} cartas salvas',
    },
  },

  letterAnalyse: {
    importTitle: 'Importar minha carta',
    importSubtitle: 'PDF / Word',
    importPicked: 'Arquivo importado: %{name}',
    importRemove: 'Remover o arquivo',
    orPaste: 'ou cole o texto dela',
    rebuiltFromPdf: 'Texto reconstituído a partir do PDF importado.',
    title: 'Analisar minha carta',
    subtitle: 'A IA avalia sua carta em relação ao cargo e diz o que corrigir primeiro.',
    resultTitre: 'Análise da minha carta',
    tabSaved: 'Minhas cartas',
    tabImport: 'Importar',
    tabPaste: 'Colar',
    importHint: 'PDF ou Word. Será guardada em Minhas cartas com a análise.',
    pasteLabel: 'Texto da sua carta',
    pastedTitle: 'Carta colada em %{date}',
    cvCrossedNone: 'Sem currículo: os factos da carta não serão verificados.',
    offerSub: 'Opcional, para uma análise mais precisa',
    optionsTitre: 'Mais opções',
    optionsSousTitre: 'Perfil do candidato',
    profilRepris: 'Perfil vindo da sua conta.',
    sourceSection: 'A CARTA A ANALISAR',
    pickSaved: 'Escolher uma carta salva',
    pickSavedEmpty: 'Nenhuma carta salva por enquanto',
    pasteTitle: 'Colar uma carta',
    pasteSubtitle: 'Uma carta escrita em outro lugar',
    pastePlaceholder: 'Cole aqui o texto da sua carta…',
    pasteSaved: 'Sua carta colada será salva em “Minhas cartas” com a análise.',
    titleLabel: 'Título da carta (opcional)',
    titlePlaceholder: 'ex. Alternância – analista de comunicação',
    offerLabel: 'Anúncio',
    offerHint: 'Sem anúncio, a análise considera a profissão desejada e as expectativas do mercado.',
    offerPlaceholder: 'Cole o anúncio aqui…',
    profileLabel: 'Seu perfil',
    profileHint: 'Ele define o nível de exigência: a carta de um aluno do fundamental não é julgada como a de um executivo.',
    metierLabel: 'Cargo visado',
    metierPlaceholder: 'ex. Analista de comunicação',
    submit: 'Analisar minha carta',
    loading: 'A IA está analisando sua carta…',
    empty: 'Adicione a carta a analisar.',
    failed: 'A análise falhou. Tente de novo.',
    cvCrossed: 'Análise cruzada com seu currículo principal.',
    cvNotCrossed: 'Nenhum currículo disponível: os fatos citados na carta não foram verificados.',
    obsolete: 'Esta carta foi alterada depois da análise. A pontuação se refere à versão anterior.',
    reanalyse: 'Refazer a análise',
    improve: 'Melhorar minha carta',
    prioritiesSection: 'Corrigir primeiro',
    errorsSection: 'Erros detectados',
    strengthsSection: 'O que já funciona',
    detailSection: 'Detalhe por critério',
    coveredSection: 'REQUISITOS ATENDIDOS',
    missingSection: 'REQUISITOS NÃO ABORDADOS',
    incoherenceSection: 'Diferenças com seu currículo',
    faults: {
      zero: 'Nenhum erro encontrado',
      one: '%{count} erro encontrado',
      other: '%{count} erros encontrados',
    },
    verdicts: {
      a_refaire: 'Refazer',
      a_retravailler: 'Precisa de ajustes',
      envoyable_apres_corrections: 'Pode enviar após correções',
      solide: 'Sólida',
      excellente: 'Excelente',
    },
    criteres: {
      C1: 'Adequação ao cargo',
      C2: 'Provas e especificidade',
      C3: 'Personalização',
      C4: 'Estrutura e tamanho',
      C5: 'Língua e estilo',
      C6: 'Protocolo e forma',
    },
    niveaux: {
      insuffisant: 'Insuficiente',
      fragile: 'Frágil',
      solide: 'Sólido',
      excellent: 'Excelente',
    },
    gravites: {
      majeure: 'Grave',
      moyenne: 'Média',
      mineure: 'Leve',
    },
  },

  // « Améliorer ma lettre » (1.2.0).
  coverLetterImprove: {
    title: 'Melhorar minha carta',
    blocked: 'Não foi possível concluir a melhoria.',
    retry: 'Tentar novamente',
    reponseInvalide: 'A resposta está incompleta. Tente novamente em instantes.',
    saveFailed: 'Não foi possível salvar.',
    copieTitre: '%{titre} (melhorada)',
    tags: { hook: 'Abertura', proof: 'Prova', keywords: 'Palavras-chave', clarity: 'Clareza', closing: 'Fechamento', subject: 'Assunto' },
    modif: { objet: 'Assunto da carta', paragraphe: 'Parágrafo %{numero}', appliquer: 'Aplicar: %{nom}' },
    revue: {
      titre: {
        one: '%{count} alteração sugerida',
        other: '%{count} alterações sugeridas',
      },
      aide: 'Aceite ou recuse cada alteração. Sua carta atual não muda: suas escolhas geram uma nova carta, adicionada a “Minhas cartas”.',
      longueur: 'Corpo da carta: %{avant} palavras antes, %{apres} palavras depois.',
    },
    pied: {
      retenues: 'Mantidas: %{count} de %{total}',
      appliquer: {
        one: 'Aplicar %{count} alteração',
        other: 'Aplicar %{count} alterações',
      },
      aucune: 'Nenhuma alteração mantida',
    },
    vide: { titre: 'Sua carta já está sólida', detail: 'A IA não encontrou nenhuma alteração útil para sugerir. Nada foi descontado da sua cota.' },
    fait: { titre: 'Nova carta salva', detail: 'Sua carta melhorada está em “Minhas cartas”. Sua carta original não mudou.', ouvrir: 'Abrir a carta' },
  },

  letterList: {
    open: 'Abrir',
    title: 'Minhas cartas',
    subtitle: 'Encontre, reabra e gerencie suas cartas salvas.',
    loadError: 'Não foi possível carregar suas cartas. Tente de novo.',
    emptyTitle: 'Nenhuma carta por enquanto.',
    emptyText: 'Gere sua primeira carta a partir de um dos seus currículos, e ela será salva aqui.',
    emptyCta: 'Criar minha carta',
    deleteTitle: 'Excluir esta carta?',
    deleteBody: '“%{title}” será excluída definitivamente.',
  },

  letterCreate: {
    erreurFormat: 'Não foi possível escrever a carta. Tente de novo: nenhum crédito foi usado.',
    modifierAnnonce: 'Editar o anúncio',
    change: 'Alterar',
    changeA11y: 'Alterar currículo',
    optionsTitre: 'Mais opções',
    optionsSousTitre: 'Perfil, organização, destinatário, e-mail',
    reprisDuCompte: 'Perfil e tipo de candidatura vindos da sua conta. O que você escolher aqui será lembrado na próxima carta.',
    failed: 'Não foi possível gerar a carta. Tente novamente.',
    annonceOr: 'ou importe o arquivo do anúncio',
    annonceFile: 'PDF / Word',
    annonceFilePicked: 'Anúncio importado: %{name}',
    annonceFileRemove: 'Remover o arquivo',
    title: 'Gerar uma carta',
    subtitle: 'Escolha um currículo, cole o anúncio e ajuste o tom.',
    resultTitle: 'Sua carta',
    resultSubtitle: 'Releia, verifique o que precisa ser verificado e depois baixe.',
    noCvTitle: 'Primeiro você precisa de um currículo',
    noCvText:
      'Sua carta usa o seu currículo para tirar suas experiências e provas. Crie ou importe um currículo e depois volte aqui.',
    goToCv: 'Ir para Meu currículo',
    toConfirm: 'Verifique antes de enviar',
    emailBlock: 'E-mail de envio',
    download: 'Baixar minha carta',
    cvUsed: 'Currículo usado',
    annonce: 'Anúncio visado',
    annoncePlaceholder: 'Cole o anúncio aqui…',
    profil: 'Perfil',
    type: 'Tipo de candidatura',
    ton: 'Tom da carta',
    langue: 'Idioma da carta',
    organisation: 'Organização desejada',
    organisationPlaceholder: 'Ex. ACME',
    metier: 'Profissão desejada',
    metierPlaceholder: 'Ex. Analista de projetos',
    recipientName: 'Destinatário — nome',
    recipientNamePlaceholder: 'Deixe em branco se não souber',
    recipientRole: 'Destinatário — cargo',
    recipientRolePlaceholder: 'Ex. Responsável pelo recrutamento',
    city: 'Sua cidade',
    cityPlaceholder: 'Ex. Lyon',
    addEmail: 'Adicionar um e-mail de envio',
    generate: 'Gerar minha carta',
    hint: 'Escolha um currículo e adicione o anúncio para começar a geração.',
    defaultTitle: 'Carta de apresentação',
    saveFailed: 'Sua carta foi gerada, mas não foi possível salvá-la.',
    exportFailed: 'A exportação falhou.',
    wordCount: '%{count} palavras',
    profils: {
      collegien: 'Ensino fundamental (estágio do 9º ano)',
      lyceen: 'Ensino médio',
      etudiant_jeune_diplome: 'Estudante ou recém-formado',
      actif_confirme: 'Profissional experiente',
      manager_cadre_dirigeant: 'Gestor ou executivo',
      reconversion: 'Transição de carreira',
    },
    types: {
      reponse_offre: 'Resposta a uma vaga',
      candidature_spontanee: 'Candidatura espontânea',
      mobilite_interne: 'Mobilidade interna',
    },
    tons: {
      classique: 'Clássico',
      chaleureux: 'Caloroso',
      direct: 'Direto',
      entrepreneurial: 'Empreendedor',
      institutionnel: 'Institucional',
      executif: 'Executivo',
    },
    langues: { francais: 'Francês', anglais: 'Inglês' },
  },

  interview: {
    startFailed: 'Não foi possível iniciar a entrevista. Tente novamente.',
    noCv: 'Você ainda não tem um currículo. Crie um primeiro para iniciar uma entrevista.',
    title: 'Entrevista com IA',
    cvUsed: 'Currículo usado',
    change: 'Trocar',
    changeA11y: 'Trocar de currículo',
    hintEmpty: 'Informe o cargo desejado para iniciar a entrevista.',
    loadError: 'Não foi possível carregar.',
    addCv: 'Adicionar outro currículo',
    posteSection: 'Cargo desejado',
    posteFile: 'Adicionar uma descrição da vaga',
    postePaste: 'Colar uma vaga de emprego',
    posteExample: 'Ex.: Analista de marketing',
    pickFile: 'PDF / Word',
    pickCaptures: 'Capturas',
    offerPlaceholder: 'Cole aqui o texto da vaga…',
    companyType: 'Tipo de empresa (opcional)',
    companyTypePlaceholder: 'startup, multinacional, consultoria, PME…',
    adaptNote: 'A IA analisa e adapta as perguntas ao cargo desejado.',
    languageLabel: 'Idioma da entrevista',
    start: 'Iniciar a entrevista',
    interlocutors: {
      recruiter: {
        label: 'Recrutador',
        points: { motivation: 'Motivação', parcours: 'Trajetória', competences: 'Competências' },
      },
      manager: {
        label: 'Gestor',
        points: { terrain: 'Prática', metiers: 'Profissão', situation: 'Estudo de caso' },
      },
    },
    quitTitle: 'Abandonar a entrevista?',
    quitBody: 'Seu progresso será perdido.',
    quitCancel: 'Continuar a entrevista',
    quitConfirm: 'Abandonar',
    muteVoice: 'Desativar a voz do recrutador',
    voixActivee: 'Voz ativada',
    downloadPdf: 'Baixar o PDF',
    pdfErreur: 'Não foi possível criar o PDF. Tente de novo.',
    voixCoupee: 'Voz desativada',
    voixReviendra: {
      lundi: 'Voz do celular por enquanto: a voz realista volta na segunda-feira.',
      mois: 'Voz do celular por enquanto: a voz realista volta no dia 1º do mês.',
      entretien: 'Voz do celular até o fim desta entrevista: a voz realista volta na próxima.',
      bientot: 'Voz do celular por enquanto: a voz realista volta em breve.',
    },
    voixRestante: {
      semaine: 'Voz realista: mais %{n} entrevistas esta semana.',
      semaineUn: 'Voz realista: mais 1 entrevista esta semana.',
      semaineAucun: 'Voz realista: acabou por esta semana. A voz do celular assume até segunda-feira.',
      mois: 'Voz realista: mais %{n} entrevistas este mês.',
      moisUn: 'Voz realista: mais 1 entrevista este mês.',
      moisAucun: 'Voz realista: acabou por este mês. A voz do celular assume até o dia 1º.',
    },
    unmuteVoice: 'Ativar a voz do recrutador',
    listen: 'Ouvir',
    finish: 'Encerrar a entrevista',
    answerPlaceholder: 'Sua resposta…',
    stopDictation: 'Parar o ditado',
    startDictation: 'Responder por voz',
    clearAnswer: 'Apagar',
    send: 'Enviar',
    strengths: 'Pontos fortes',
    improvements: 'A melhorar',
    tips: 'Dicas',
    restart: 'Recomeçar',
    phasePresentation: 'Fase de apresentação',
    phaseOngoing: 'Entrevista em andamento',
    feedbackHeader: 'Seu feedback',
    overallLabel: 'Avaliação geral',
    dureeMin: '%{n} min',
  },

  profile: {
    fallbackName: 'Seu perfil',
    personalInfo: 'Informações pessoais',
    personalInfoSubtitle: 'Gerencie suas informações e preferências',
    subscription: 'Minha assinatura',
    subscriptionSubtitle: 'Gerencie seu plano e suas faturas',
    activity: 'MINHA ATIVIDADE ESTE MÊS',
    coaching: 'MEU COACHING',
    coachingHint: 'O horário é combinado por e-mail.',
    bookSession: 'Agendar uma sessão',
    feedbackTitle: 'Tem perguntas ou sugestões de melhoria para nós?',
    feedbackSubtitle: 'Sua opinião conta!',
    avatarSheet: 'Foto ou fundo do avatar',
    importFailed: 'Não foi possível importar. Tente de novo.',
    stats: {
      cvCreated: 'Currículos criados',
      interviews: 'Entrevistas IA',
      cvScores: 'Avaliações de currículo',
      linkedinScores: 'Avaliações LinkedIn',
    },
    noSession: 'Nenhuma sessão agendada',
    sessionFallback: 'Sessão',
    lastSession: 'Última sessão agendada em %{date} — %{formule}',
  },

  personalInfo: {
    firstName: 'Nome',
    firstNamePlaceholder: 'Seu nome',
    lastName: 'Sobrenome',
    lastNamePlaceholder: 'Seu sobrenome',
    email: 'E-mail de contato',
    emailPlaceholder: 'voce@exemplo.com',
    phone: 'Telefone',
    phonePlaceholder: '06 12 34 56 78',
    address: 'Endereço / cidade',
    addressPlaceholder: 'Ex. Paris',
    linkedin: 'URL do LinkedIn',
    linkedinPlaceholder: 'https://www.linkedin.com/in/…',
  },

  profileCompletion: {
    title: 'Só mais uma coisa',
    birthDatePlaceholder: 'DD/MM/AAAA',
    continue: 'Continuar',
    later: 'Mais tarde',
    saveFailed: 'Não foi possível salvar.',
  },

  notifications_screen: {
    title: 'Notificações',
    empty: 'Nenhuma notificação nova por enquanto.',
    settingsSubtitle: 'Seus lembretes e dicas para avançar na sua busca.',
    tips: 'DICAS',
  },

  auth: {
    signup: {
      title: 'Crie sua conta,',
      titleAccent: 'vá em frente.',
      subtitle: 'Melhore seu currículo, prepare suas entrevistas e avance rumo ao seu contrato.',
      cta: 'Criar minha conta',
    },
    signupEtablissement: {
      title: 'Crie a conta da sua',
      titleAccent: 'instituição',
      subtitle: 'e ofereça aos seus estudantes uma ferramenta para preparar as candidaturas.',
    },
    signupPartenaire: {
      title: 'Crie a conta de',
      titleAccent: 'parceiro',
      subtitle: 'e ofereça aos jovens que acompanha uma ferramenta para preparar as candidaturas.',
    },
    signin: {
      title: 'Continue',
      titleAccent: 'se destacando.',
      subtitle: 'Retome suas melhorias e avance rumo à sua próxima entrevista.',
      cta: 'Entrar',
    },
    pending:
      'Se este endereço ainda não estiver em uso, você vai receber um e-mail de confirmação. Confira também a pasta de spam.',
    invalidEmail: 'Endereço de e-mail inválido.',
    invalidPassword: 'Senha inválida.',
    emailLabel: 'E-mail',
    emailPlaceholder: 'nome@email.com.br',
    passwordLabel: 'Senha',
    hidePassword: 'Ocultar a senha',
    showPassword: 'Mostrar a senha',
    tabSignin: 'Entrar',
    tabSignup: 'Criar conta',
    forgot: {
      link: 'Esqueceu a senha?',
      title: 'Recupere seu',
      titleAccent: 'acesso.',
      subtitle: 'Informe seu e-mail e enviaremos um link para você escolher uma nova senha.',
      cta: 'Enviar o link',
      sent: 'Se existir uma conta com este endereço, um e-mail está a caminho com um link para escolher uma nova senha. Confira a caixa de spam e abra o link neste celular.',
      back: 'Voltar para o login',
    },
    newPassword: {
      title: 'Escolha sua nova',
      titleAccent: 'senha.',
      subtitle: 'Ela substitui a anterior.',
      newLabel: 'Nova senha',
      confirmLabel: 'Confirme a senha',
      tooShort: 'A senha deve ter pelo menos %{min} caracteres.',
      mismatch: 'As duas senhas não são iguais.',
      cta: 'Salvar',
      cancel: 'Cancelar e sair',
    },
    or: 'ou',
    google: 'Continuar com Google',

    error: {
      invalidCredentials: 'E-mail ou senha incorretos.',
      emailNotConfirmed:
        'Sua conta ainda não foi confirmada. Abra o link que enviamos por e-mail — confira também a pasta de spam.',
      emailInvalid: 'Este endereço de e-mail não é válido.',
      validationFailed: 'Verifique seu e-mail e sua senha.',
      weakPassword: 'Senha fraca demais. Escolha uma mais longa, com números e letras.',
      rateLimit: 'Tentativas demais. Tente de novo em alguns minutos.',
      userBanned: 'Esta conta está suspensa. Escreva para nós para saber mais.',
      network: 'Sem conexão. Verifique sua internet e tente de novo.',
      confirmExpired:
        'Este link de confirmação não é mais válido. Recomece a criação da sua conta com o mesmo endereço para receber um novo.',
      confirmWrongDevice: 'Abra este link no celular em que você criou sua conta.',
      resetExpired: 'Este link de redefinição não é mais válido. Peça um novo em “Esqueceu a senha?”.',
      resetWrongDevice: 'Abra o e-mail mais recente, no celular em que você pediu a redefinição.',
      samePassword: 'Escolha uma senha diferente da anterior.',

      serverSignup:
        'Não conseguimos criar sua conta no momento — o problema é do nosso lado, não do seu. Tente de novo em alguns minutos ou continue com %{providers}.',
      serverSignin:
        'Não conseguimos fazer seu login no momento — o problema é do nosso lado, não do seu. Tente de novo em alguns minutos ou continue com %{providers}.',
      serverOauth:
        'O login não foi concluído — o problema é do nosso lado, não do seu. Tente de novo em alguns minutos ou entre com seu e-mail e sua senha.',
      popupBlocked:
        'Seu navegador bloqueou a janela de login do Google. Permita pop-ups para este site e tente entrar de novo — ou entre com seu e-mail e sua senha.',
      appleUnknown:
        'O login com a Apple não foi concluído. Verifique se você está conectado ao iCloud neste aparelho e se seu ID Apple tem autenticação de dois fatores. Se não, entre com seu e-mail e sua senha.',
      serverSignout: 'Não foi possível sair da conta. Tente de novo em alguns minutos.',
      serverConfirm:
        'Não conseguimos confirmar sua conta no momento — o problema é do nosso lado, não do seu. Tente de novo em alguns minutos ou continue com %{providers}.',
      serverReset:
        'Não conseguimos redefinir sua senha no momento — o problema é do nosso lado, não do seu. Tente de novo em alguns minutos.',

      googleStart: 'Não conseguimos abrir o login do Google. Tente de novo em alguns minutos.',
      googleResponse: 'A resposta do Google veio incompleta. Tente de novo.',
      appleResponse: 'A resposta da Apple veio incompleta. Tente de novo.',
      deleteFailed:
        'Sua conta não foi excluída: a operação falhou antes de qualquer exclusão, seus dados estão intactos. Tente de novo em alguns minutos.',
      deleteAppleCancelled: 'Exclusão cancelada: confirme com a Apple para excluir sua conta.',

      providersIos: 'Google ou Apple',
      providersAndroid: 'Google',
    },
  },

  onboarding: {
    highlights: { cv: 'Currículo', linkedin: 'LinkedIn', interview: 'Entrevistas', coach: 'Coach' },
    title: 'Seu perfil merece',
    mots: {
      reponses: 'respostas.',
      cv: 'um CV que passa.',
      entretien: 'uma entrevista.',
      contrat: 'um contrato.',
    },
    sousTitre: 'A gente te acompanha do currículo até a assinatura do seu contrato.',
    wheel: {
      cv: 'Currículo ATS',
      linkedin: 'LinkedIn',
      interview: 'Entrevistas IA',
      coach: 'Coach especialista',
      offers: 'Vagas',
      letter: 'Carta',
      center: 'Seu contrato',
    },
    quickSignup: 'Cadastro rápido',
    dataProtected: 'Dados protegidos',
    createAccount: 'Criar minha conta',
    haveAccount: 'Já tenho uma conta',
  },


  acceptTerms: {
    titleOutdated: 'Nossos termos mudaram',
    titleFirst: 'Quase lá ✨',
    changedPrefix: 'Nossos ',
    changedTermsLink: 'termos de uso',
    changedMiddle: ' e nossa ',
    changedPrivacyLink: 'política de privacidade',
    changedSuffix:
      ' foram atualizados (versão %{version}). Leia e depois aceite para continuar usando o app.',
    firstPrefix: 'Simplificamos nossos termos para ',
    firstAccent: 'proteger melhor seus dados',
    pillars: {
      data: 'Dados\nprotegidos',
      transparency: 'Transparência\ntotal',
      consent: 'Nenhuma ação\nsem você',
    },
    docTerms: 'Termos de uso',
    docPrivacy: 'Política de privacidade',
    accept: 'Continuar',
    refuseOutdated: 'Recusar e sair',
    refuseFirst: 'Mais tarde, sair',
    help: 'Precisa de ajuda?',
    deleteAccount: 'Excluir minha conta',
    saveFailed: 'Não foi possível salvar. Tente de novo.',
  },

  legalHeaders: {
    documentsEnFrancais: 'Documentos disponíveis em francês',
    privacy: 'Privacidade e dados',
    terms: 'Termos de uso',
    dataDeletion: 'Exclusão de conta',
  },

  plans: {
    free: {
      tagline: 'Para começar',
      price: 'Grátis',
      period: 'para sempre',
      entretiens: '1 entrevista IA / mês, com a voz natural do recrutador',
      features: [
        '1 avaliação e 1 importação de currículo / mês',
        '1 auditoria LinkedIn / mês',
        '1 carta e 1 análise de carta / mês',
        '1 exportação em PDF / mês',
        'Sem criação de currículo por IA',
        'Modelos básicos',
      ],
      featuresOffres: ['Vagas de alternância e de emprego: acesso livre', '2 candidaturas IA / mês (aba Vagas)'],
    },
    plus: {
      tagline: 'O equilíbrio certo',
      period: '/ mês',
      entretiens: '8 entrevistas IA / mês',
      voix: '2 entrevistas por semana com a voz realista do recrutador',
      features: [
        '5 criações e 5 importações de currículo / mês',
        '5 avaliações de currículo / mês',
        '5 auditorias LinkedIn / mês',
        '5 cartas e 5 análises de carta / mês',
        '20 reescritas de seções por IA / mês',
        'Exportação ilimitada e todos os modelos',
      ],
      featuresOffres: ['15 candidaturas IA / mês (aba Vagas)'],
    },
    pro: {
      tagline: 'Para ir até o fim da sua busca',
      period: '/ mês',
      entretiens: '20 entrevistas IA / mês',
      voix: '5 entrevistas por semana com a voz realista do recrutador',
      ecart: '20 entrevistas e 60 candidaturas IA por mês',
      ecartSansOffres: '20 entrevistas IA por mês',
      features: [
        'Ilimitado: currículos, avaliações, auditorias LinkedIn, cartas e análises',
        'Reescritas por IA e exportações ilimitadas',
        'Modelos premium',
      ],
      featuresOffres: ['60 candidaturas IA / mês (aba Vagas)'],
    },
    school: {
      features: [
        'Todas as funcionalidades, para todos os alunos',
        'Ideal para escolas e CFAs',
        'Cobrança por usuário',
        'Licença',
        'Painel de acompanhamento da instituição',
        'Relatório trimestral',
        'Personalização',
      ],
    },
  },

  plansExtra: {
    proAnnual: {
      name: 'Pro anual',
      period: '/ano',
      badge: '−%{pourcentage}%',
      equivalent: 'ou seja, %{monthly}/mês × 12',
      cta: 'Assinar o anual',
    },
  },

  paywall: {
    mentionAbonnement: 'Assinatura mensal ou anual de acordo com o plano escolhido, renovada automaticamente pelo mesmo período, salvo cancelamento com pelo menos 24 horas de antecedência do fim do período atual. Você pode cancelar a qualquer momento no gerenciamento de assinaturas da App Store ou do Google Play; o cancelamento entra em vigor no fim do período atual.',
    tonPlan: 'Seu plano',
    bientotSurPlateforme: 'As assinaturas chegam em breve a esta plataforma.',
    currentPlan: 'Seu plano atual: ',
    title: 'Suba de nível',
    capReached: 'Você não tem mais usos este mês. Seu contador reinicia em %{date}.',
    notIncluded: 'Esta funcionalidade não está incluída no seu plano atual.',
    tabIndividual: 'Individual',
    tabSchools: 'Escolas',
    restore: 'Restaurar minhas compras',
    contact: 'Fale com a gente',
    recommended: 'Recomendado',
    subscribe: 'Assinar',
    pendingApproval: 'Compra aguardando aprovação.',
    prixIndisponibles: 'Os preços não estão disponíveis agora. Tente novamente em instantes.',
    genericError: 'Ocorreu um erro.',
    activated: 'Assinatura ativada 🎉',
    restored: 'Compras restauradas.',
    activating: 'Compra recebida. Ativando sua assinatura…',
    activationEnCours: 'Compra recebida. Sua assinatura está sendo ativada: pode levar um instante.',
    restoreEnCours: 'Restauração enviada. Se houver uma assinatura vinculada à sua conta, ela aparecerá em instantes.',
    schoolMailSubject: 'Offre établissement Horméia',
  },

  coaching: {
    tarifsIndisponibles: 'Preços temporariamente indisponíveis.',
    reessayer: 'Tentar de novo',
    montantIncoherent:
      'O pagamento está temporariamente indisponível: o preço exibido não corresponde ao valor a cobrar. Preferimos não cobrar nada enquanto isso não for resolvido.',
    retourSuccesTitre: 'Pagamento confirmado',
    retourSuccesCorps:
      'Seu pagamento foi recebido. Você vai receber um e-mail de confirmação com os detalhes da sua sessão.',
    retourEnCoursTitre: 'Pagamento em processamento',
    retourEnCoursCorps:
      'Seu banco ainda não confirmou o pagamento, o que é normal com alguns meios de pagamento. Não tente de novo: você seria cobrado duas vezes. Você vai receber um e-mail assim que for confirmado.',
    retourEchecTitre: 'Pagamento recusado',
    retourEchecCorps:
      'O pagamento não foi concluído e você não foi cobrado. Você pode tentar de novo, com o mesmo meio de pagamento ou outro.',
    retourAnnuleTitre: 'Pagamento cancelado',
    retourAnnuleCorps: 'Você interrompeu o pagamento e não foi cobrado. Seu horário continua disponível.',
    retourVersCoaching: 'Voltar ao coaching',
    title: 'Coaching',
    pickSlot: 'Escolher meu horário',
    soon: 'Disponível em breve',
    slotBeforePayment: 'Seu horário é confirmado antes de qualquer pagamento.',
    howTitle: 'Como funciona',
    step1: 'Você escolhe seu horário',
    step2: 'Você valida sua reserva',
    step3: 'Você paga sua sessão',
    pickFormule: 'Escolha seu formato',
    formuleNotFound: 'Formato não encontrado. Volte e escolha um formato.',
    reserve: 'Reservar',
    checkingSlot: 'Estamos verificando seu horário na agenda…',
    slotConfirmed: 'Seu horário está confirmado. Só falta validar o pagamento.',
    chosenFormule: 'FORMATO ESCOLHIDO',
    email: 'E-MAIL',
    searching: 'O QUE VOCÊ PROCURA (OPCIONAL)',
      payUnavailable:
        'O pagamento está temporariamente indisponível. Tente novamente mais tarde.',
    paid: 'Pagamento concluído ✓. Sua reserva está confirmada, o Julien vai entrar em contato.',
    processing: 'Seu pagamento está sendo processado. Não pague uma segunda vez: você receberá uma confirmação assim que for validado.',
    securePayment: 'Pagamento seguro com cartão (Stripe). Você é cobrado uma única vez.',
    nameRequired: 'Informe seu nome.',
    invalidEmail: 'Endereço de e-mail inválido.',
    paymentFailed: 'Não foi possível pagar. Tente de novo.',
    paymentFailedShort: 'Não foi possível pagar.',
  },

  cvCreateFlow: {
    titre: 'Criar um currículo',
    etapeSur: '%{n} de %{total}',
    step1Titre: 'Qual cargo você busca?',
    step1Sous: 'A IA adapta todo o currículo a esse cargo.',
    step2Titre: 'Sua trajetória',
    step2Sous: 'Não precisa escrever bem: a IA reformula para você.',
    step3Titre: 'Para terminar',
    step3Sous: 'Tudo é opcional, você pode gerar seu currículo agora.',
    nameFromProfile: 'Do seu perfil',
    importTitre: 'Já tem um currículo?',
    importSous: 'Importe-o, é mais rápido.',
    roleRequired: 'Informe o cargo desejado para continuar.',
    tuileProjet: 'Um projeto',
    tuileAutre: 'Outra coisa',
    optionnel: 'Opcional',
    ajoute: 'Adicionado',
    nameLabel: 'Nome no currículo',
    namePlaceholder: 'Ex. Jean Dupont',
    languageLabel: 'Idioma do currículo',
    languageHint: 'Você pode responder em francês e receber um currículo em inglês, ou o contrário.',
    contratLabel: 'Você procura…',
    contratHint: 'Opcional. Toque de novo para remover.',
    contrats: { alternance: 'Alternância', stage: 'Estágio', cdi: 'CDI (efetivo)', cdd: 'CDD (prazo determinado)' },
    generate: 'Gerar meu currículo',
    next: 'Continuar',
    buildingTitle: 'Estamos montando seu currículo.',
    buildingHint: 'Alguns segundos, prometemos.',
    editTitle: 'Editar meu currículo',
    cancel: 'Cancelar',
    save: 'Salvar',
    readyTitle: 'Seu currículo está pronto',
    atsScore: 'Pontuação ATS',
    edit: 'Editar',
    saving: 'Salvando…',
    retrySave: 'Tentar salvar de novo',
    download: 'Exportar meu currículo',
    condensePourUnePage: 'Conteúdo condensado para caber em uma página',
    aiAdvice: 'DICAS DA IA',
    targetOffer: 'MIRAR UMA VAGA (OPCIONAL)',
    offerPlaceholder: 'Cole a vaga aqui…',
    offerFile: 'Vaga (PDF/Word)',
    offerCaptures: 'Capturas',
    recommendCta: 'Recomendações para esta vaga',
    recommendSection: 'RECOMENDAÇÕES PARA ESTA VAGA',
    testInScore: 'Testar este currículo em Avaliar meu currículo',
    skillPlaceholder: 'Ex. SQL',
    interestPlaceholder: 'Ex. Corrida de trilha',
    noterCe: 'Avaliar este currículo',
    savedIn: 'Salvo em Meus currículos',
    editsSaved: 'Alterações salvas',
    saveFailed: 'Seu currículo foi gerado, mas não foi possível salvá-lo.',
    editSaveFailed: 'Não foi possível salvar.',
    offerHint: 'Adicione uma vaga: a IA sugere como valorizar sua trajetória para essa vaga (sem inventar nada). Cabe a você aplicar pela edição.',
  },

  help: {
    title: 'Ajuda e contato',
    faqSection: 'PERGUNTAS FREQUENTES',
    contactSection: 'FALE COM A GENTE',
    writeUs: 'Escreva para nós',
    sendEmail: 'Enviar um e-mail',
    mailSubject: 'Aide — Horméia',
    faq: {
      ats: {
        q: 'Como minha pontuação ATS é calculada?',
        a: 'A IA analisa seu currículo pelos critérios que os sistemas de triagem de candidaturas (ATS) priorizam: presença das palavras-chave do cargo, estrutura clara, seções padrão e resultados com números. A pontuação reflete essa compatibilidade e vem com dicas concretas para melhorá-la.',
      },
      privacy: {
        q: 'Meus dados são confidenciais?',
        a: 'Seus currículos e informações servem para fazer funcionar os recursos do app (avaliação, criação, entrevista). Uma página dedicada à privacidade e à gestão dos seus dados será publicada em breve; até lá, escreva para nós se tiver qualquer dúvida.',
      },
      coaching: {
        q: 'Como funciona o coaching?',
        a: 'O coaching é um acompanhamento humano com Julien Dauzats, especialista em RH — separado dos recursos de IA. Você escolhe um formato, agenda pelo app, e a conversa acontece por videochamada.',
      },
    },
  },

  misc: {
    personalInfoTitle: 'Informações pessoais',
    save: 'Salvar',
    coachNamePlaceholder: 'Seu nome',
    coachGoalPlaceholder: 'Ex. preparar uma entrevista de dados na semana que vem',
    atsCompatible: 'Compatível com ATS',
    completeProfile: 'Complete seu perfil',
    completeProfileCta: 'Completar meu perfil',
    later: 'Mais tarde',
    feedbackQuestion: 'Este resultado foi útil para você?',
    send: 'Enviar',
    importPhoto: 'Importar uma foto',
    orColour: 'OU UM FUNDO COLORIDO',
    add: 'Adicionar',
    yearPlaceholder: 'Ano',
    comingSoon: 'Disponível em breve.',
  },

  progress: {
    linkedinKitSections: [
      'Escrevendo seu título…',
      'Escrevendo sua seção Sobre…',
      'Reescrevendo suas experiências…',
      'Escolhendo suas competências…',
      'Ajustando suas preferências de busca…',
      'Preparando suas dicas rápidas…',
      'Finalizando seu kit…',
    ],

    linkedinSections: [
      'Lendo seu perfil…',
      'Identificando os erros clássicos…',
      'Preparando sua pontuação e suas prioridades…',
    ],

    elapsed: {
      start: 'Estamos montando seu currículo…',
      mid: 'Só mais alguns segundos…',
      long: 'Está demorando um pouco mais que o normal, seguimos trabalhando.',
    },
    letter: {
      start: 'Estamos escrevendo sua carta…',
      mid: 'Só mais alguns segundos…',
      long: 'Está demorando um pouco mais que o normal, seguimos trabalhando.',
    },
    linkedin: {
      start: 'Estamos analisando seu perfil…',
      mid: 'Só mais alguns segundos…',
      long: 'Está demorando um pouco mais que o normal, seguimos trabalhando.',
    },
    improve: {
      start: 'Estamos otimizando seu currículo…',
      mid: 'Só mais alguns segundos…',
      long: 'Está demorando um pouco mais que o normal, seguimos trabalhando.',
    },
    improveLetter: {
      start: 'Estamos melhorando sua carta…',
      mid: 'Só mais alguns segundos…',
      long: 'Está demorando um pouco mais que o normal, seguimos trabalhando.',
    },
    received: 'Recebido',
    saved: 'Salvo',
  },

  // Licence accordée par l'établissement de l'étudiant (lot 4 du back-office). Aucun lien d'achat ici.
  licenceEcole: {
    title: 'Licença da escola',
    loading: 'A carregar…',
    loadError: 'Não foi possível carregar a tua licença. Tenta de novo.',
    retry: 'Tentar de novo',
    intro: 'A tua escola enviou-te um código de convite? Introduz-o para ativar a tua licença.',
    codeLabel: 'Código de convite',
    codePlaceholder: 'XXXX-XXXX-XXXX',
    activer: 'Ativar a minha licença',
    activating: 'A ativar…',
    voitTitre: 'O que a tua escola vê',
    voit: [
      'A tua situação (à procura, em contrato, contrato terminado) e as tuas datas-chave, registadas pela escola.',
      'As ações de acompanhamento que fez por ti: chamadas, reuniões, ofertas enviadas.',
      'A tua atividade na app: os dias em que a usas. É visível assim que ativas a licença, porque é a tua escola que ta fornece.',
    ],
    neVoitPas: 'Nunca vê o conteúdo dos teus CV, cartas ou entrevistas.',
    active: 'Licença ativa',
    via: 'Fornecida por %{nom}',
    employeurTitre: 'A tua empresa',
    employeurContrat: 'A tua escola associou-te a %{nom} desde %{date}.',
    employeurVoitTitre: 'O que a tua empresa veria se o autorizares',
    employeurVoit: [
      'O teu nome, o teu curso, a tua escola e as datas do teu contrato.',
      'A tua situação (à procura, em contrato, contrato terminado).',
    ],
    employeurNeVoitPas: 'Nunca vê o teu e-mail, o registo de acompanhamento da tua escola, as tuas candidaturas, a tua atividade na app, nem o conteúdo dos teus CV, cartas ou entrevistas.',
    employeurConsentTitre: 'Partilhar a minha situação com a minha empresa',
    employeurConsentBody: 'Desativado por predefinição. Podes retirá-lo a qualquer momento: o teu contrato e a tua licença não mudam. A tua escola só vê se aceitaste ou não.',
    employeurConsentError: 'Não foi possível guardar a tua escolha. Tenta de novo.',
    succes: 'A tua licença %{nom} está ativada.',
    erreurs: {
      code_invalide: 'Este código não é válido. Verifica-o ou pede um novo à tua escola.',
      invitation_expiree: 'Este convite expirou. Pede um novo à tua escola.',
      abonnement_actif: 'Já tens uma subscrição ativa. Cancela-a nas definições da App Store ou do Google Play e tenta de novo.',
      plus_de_sieges: 'A tua escola não tem lugares disponíveis. Contacta-a.',
      organisation_inactive: 'A licença da tua escola não está ativa neste momento. Contacta-a.',
      inconnu: 'Não foi possível ativar agora. Tenta de novo.',
    },
  },

  settings: {
    title: 'Configurações',
    sections: {
      account: 'Conta',
      preferences: 'Preferências',
      admin: 'Administração',
      danger: 'Zona de perigo',
    },
    privacy: 'Privacidade e dados',
    licenceEcole: 'Licença da escola',
    dataDeletion: 'Exclusão de conta e de dados',
    notifications: 'Notificações',
    adminDashboard: 'Painel admin',
    signOut: 'Sair',
    deleteAccount: 'Excluir minha conta',
    deleting: 'Excluindo…',
    deleteFailedTitle: 'Não foi possível excluir',
    deleteConfirmTitle: 'Excluir sua conta?',
    deleteConfirmBody: 'Sua conta será desativada na hora e seus dados excluídos definitivamente após 29 dias. Sua assinatura não é cancelada automaticamente: cancele nas configurações da App Store ou do Google Play.',
    language: {
      label: 'Idioma do app',
      hint: 'Os documentos que você gera mantêm o próprio idioma, escolhido no momento da criação.',
      relanceTitre: 'Reiniciar o app?',
      relanceTexte: 'Para mudar para este idioma, o Horméia precisa reiniciar. Seus currículos, cartas e configurações salvos são mantidos; uma entrevista ou um texto não salvo em andamento será perdido.',
      relanceOk: 'Reiniciar',
    },
  },
  update: {
    banner: {
      title: 'Uma nova versão está disponível',
      body: 'Atualize a Horméia para aproveitar as últimas melhorias.',
      action: 'Atualizar',
      dismiss: 'Mais tarde',
      dismissA11y: 'Ocultar este convite para atualizar',
    },
    blocking: {
      title: 'Esta versão não é mais compatível',
      body: 'A Horméia evoluiu desde a instalação desta versão, que não consegue mais funcionar direito com o serviço. Não é uma falha, e não há nada para consertar do seu lado.',
      reassurance: 'Seus dados e documentos estão intactos: você vai encontrá-los assim que abrir a nova versão.',
      action: 'Instalar a atualização',
    },
  },
  quota: {
    resetDate: '1º de %{monthYear}',
    capReachedNoUpgrade:
      'Você atingiu o limite do seu plano para esta funcionalidade. Seu contador reinicia em %{date}.',
    notIncludedNoUpgrade: 'Esta funcionalidade não está incluída na sua licença.',
  },
  errors: {
    network: 'Sem conexão. Verifique sua conexão e tente de novo.',
    server: 'O serviço está temporariamente indisponível. Tente de novo em instantes.',
    aiBadOutput: 'Não foi possível usar a resposta da IA. Refaça a análise.',
    profilTropEloigne: 'Esta vaga está distante demais do seu currículo para escrever uma carta convincente.',
    invalidRequest: 'Verifique as informações digitadas e tente de novo.',
    ficheTropLongue: 'A descrição da vaga é longa demais: no máximo 20 páginas ou 10.000 caracteres.',
    unauthorized: 'Sua sessão expirou. Entre de novo.',
  },

  nouveautes: {
    titre: 'Novidades do Horméia',
    offresTitre: 'As vagas de alternância',
    offresTexte: 'Encontre vagas de alternância e de emprego que combinam com o seu perfil e prepare sua candidatura com a IA.',
    voirOffres: 'Ver as vagas',
    voixTitre: 'A voz do recrutador',
    voixTexte: 'As simulações de entrevista agora têm uma voz realista. De acordo com o seu plano: 1 entrevista por mês no Free, 2 por semana no Accélérateur ou no École e 5 por semana no Pro.',
    languesTitre: 'Novos idiomas',
    languesTexte: 'O Horméia agora está disponível em espanhol, português, chinês e árabe. Você pode mudar o idioma nas Configurações.',
    politiqueTitre: 'Política de privacidade',
    politiqueTexte: 'Nossa política de privacidade foi atualizada para descrever a medição de uso do aplicativo. Você pode consultá-la a qualquer momento nas Configurações.',
    voirPolitique: 'Ler a política',
    bouton: 'Vamos lá',
  },
};
